import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MonitorSpeaker, Brain, Code2, Database, Shield, Rocket, Zap, Settings, BarChart3, GitBranch, Users, CheckCircle, Clock, AlertTriangle, Terminal, Activity, ChevronRight, Bot, Cpu, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Link, useNavigate } from "react-router-dom";
import { useCredits } from './hooks/useCredits';
import CreditsManager from './CreditsManager';
import { Plus, X } from 'lucide-react';

// Auto-detect backend URL based on current environment
const getBackendURL = () => {
  // TEMPORARY HARDCODE FIX - Force correct backend URL
  const correctBackendUrl = 'https://6c1f0f70-88de-468f-b801-d4c2bfd41f7b.preview.emergentagent.com';
  console.log('🚨 HARDCODED BACKEND URL (temporary fix):', correctBackendUrl);
  return correctBackendUrl;
  
  // FORCE use of environment variable if set (CRITICAL FIX)
  const envBackendUrl = process.env.REACT_APP_BACKEND_URL;
  if (envBackendUrl && envBackendUrl.trim() !== '') {
    console.log('🔧 USING ENVIRONMENT VARIABLE backend URL:', envBackendUrl);
    return envBackendUrl.trim();
  }
  
  console.log('❌ No environment variable found, using auto-detection');
  
  // Get current location info
  const currentDomain = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const currentPort = window.location.port;
  
  console.log('🔍 Auto-detecting backend URL for domain:', currentDomain);
  
  // Production domain mapping
  if (currentDomain === 'tokenos.ai' || currentDomain === 'www.tokenos.ai') {
    console.log('🎯 Production domain detected - using HTTPS');
    return 'https://tokenos.ai';
  }
  
  // Development/localhost
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    const frontendPort = currentPort || '3000';
    const backendPort = frontendPort === '3000' ? '8001' : '8001'; // Always use 8001 for backend
    const backendUrl = `${currentProtocol}//${currentDomain}:${backendPort}`;
    console.log('🧪 Development environment detected - backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Preview/staging domains (Emergent platform)
  if (currentDomain.includes('.preview.emergentagent.com') || 
      currentDomain.includes('.emergentagent.com')) {
    const backendUrl = `${currentProtocol}//${currentDomain}${currentPort ? ':' + currentPort : ''}`;
    console.log('🏗️ Staging/preview environment detected - backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Final fallback: use same domain with HTTPS if possible
  const protocol = currentProtocol === 'https:' ? 'https:' : 'https:'; // Prefer HTTPS
  const backendUrl = `${protocol}//${currentDomain}${currentPort ? ':' + currentPort : ''}`;
  console.log('🔄 Using current domain as fallback - backend URL:', backendUrl);
  return backendUrl;
};

const BACKEND_URL = getBackendURL();
console.log('🚀 TokenOS Frontend - Auto-detected backend URL:', BACKEND_URL);

// Always append /api to the backend URL since all our endpoints use this prefix
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
console.log('🚀 TokenOS Frontend - Final API URL:', API);

// Advanced AI Console with Model Selection
const AdvancedConsole = ({ publicKey, connected }) => {
  const navigate = useNavigate(); // Add navigation hook
  const [activeAgent, setActiveAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedModel, setSelectedModel] = useState('claude-4-sonnet'); // Default to latest for advanced console
  const [isLoading, setIsLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState('initializing');
  const [agentInput, setAgentInput] = useState('');
  const [selectedProjectType, setSelectedProjectType] = useState('defi_protocol');

  // AI Model configurations
  const claudeModelSettings = {
    'claude-3-5-haiku-20241022': { 
      name: 'Claude 3.5 Haiku', 
      multiplier: 0.4,  // Updated to match backend
      description: 'Fast and efficient for simple tasks',
      badge: 'FAST'
    },
    'claude-3-5-sonnet-20241022': { 
      name: 'Claude 3.5 Sonnet', 
      multiplier: 0.8,  // Updated to match backend
      description: 'Latest Sonnet with improved reasoning',
      badge: 'IMPROVED'
    },
    'claude-3-7-sonnet': { 
      name: 'Claude 3.7 Sonnet', 
      multiplier: 1.0,  // Updated to match backend
      description: 'Advanced model with hybrid reasoning',
      badge: 'ADVANCED'
    },
    'claude-4-sonnet': { 
      name: 'Claude 4 Sonnet', 
      multiplier: 1.2,  // Updated to match backend
      description: 'Superior coding and reasoning capabilities',
      badge: 'LATEST'
    },
    'claude-3-opus': { 
      name: 'Claude 3 Opus', 
      multiplier: 1.4,  // Updated to match backend
      description: 'Most powerful Claude 3 model',
      badge: 'PREMIUM'
    },
    'claude-4-opus': { 
      name: 'Claude 4 Opus', 
      multiplier: 1.6,  // Updated to match backend
      description: 'Best coding model with sustained performance',
      badge: 'ULTIMATE'
    }
  };

  const getBadgeColor = (badge) => {
    const colors = {
      'FAST': 'bg-green-500/20 text-green-300',
      'IMPROVED': 'bg-purple-500/20 text-purple-300',
      'ADVANCED': 'bg-orange-500/20 text-orange-300',
      'LATEST': 'bg-cyan-500/20 text-cyan-300',
      'PREMIUM': 'bg-red-500/20 text-red-300',
      'ULTIMATE': 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-pink-300'
    };
    return colors[badge] || 'bg-gray-500/20 text-gray-300';
  };

  // Credits and modal state
  const { credits: userCredits, refreshCredits } = useCredits();
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  // Advanced AI agent configurations with advanced development capabilities
  const agentConfigs = {
    smart_contract: {
      name: 'Smart Contract Specialist',
      icon: Code2,
      color: 'from-blue-500 to-blue-700',
      description: 'Advanced Solidity development with security auditing',
      capabilities: ['Gas Optimization', 'Security Auditing', 'ERC Standards', 'MEV Protection'],
      advancedFeatures: ['Multi-chain deployment', 'Auto-testing', 'Vulnerability scanning']
    },
    defi_protocol: {
      name: 'DeFi Protocol Builder',
      icon: BarChart3,
      color: 'from-green-500 to-green-700',
      description: 'Complete DeFi protocol architecture and implementation',
      capabilities: ['AMM Design', 'Yield Farming', 'Liquidity Mining', 'Governance Systems'],
      advancedFeatures: ['Cross-chain bridges', 'MEV protection', 'Automated rebalancing']
    },
    token_launch: {
      name: 'Token Launch Expert',
      icon: Rocket,
      color: 'from-purple-500 to-purple-700',
      description: 'End-to-end token launch strategy and execution',
      capabilities: ['Tokenomics Design', 'Launch Strategy', 'Community Building', 'Airdrop Management'],
      advancedFeatures: ['Anti-bot protection', 'Dynamic pricing', 'Vesting schedules']
    },
    security_audit: {
      name: 'Security Auditor',
      icon: Shield,
      color: 'from-red-500 to-red-700',
      description: 'Comprehensive security audits with formal verification',
      capabilities: ['Vulnerability Assessment', 'Risk Analysis', 'Formal Verification', 'Exploit Prevention'],
      advancedFeatures: ['Real-time monitoring', 'Threat intelligence', 'Emergency response']
    },
    project_manager: {
      name: 'Project Architect',
      icon: Settings,
      color: 'from-orange-500 to-orange-700',
      description: 'AI-powered project planning and orchestration',
      capabilities: ['Workflow Management', 'Resource Allocation', 'Timeline Planning', 'Team Coordination'],
      advancedFeatures: ['Predictive analytics', 'Auto-scaling', 'Risk mitigation']
    },
    code_analyzer: {
      name: 'Code Optimizer',
      icon: Brain,
      color: 'from-indigo-500 to-indigo-700',
      description: 'Advanced code quality analysis and optimization',
      capabilities: ['Performance Analysis', 'Best Practices', 'Refactoring', 'Tech Debt Analysis'],
      advancedFeatures: ['ML-driven optimization', 'Pattern recognition', 'Auto-documentation']
    },
    deployment: {
      name: 'Deployment Specialist',
      icon: MonitorSpeaker,
      color: 'from-teal-500 to-teal-700',
      description: 'Advanced deployment automation and infrastructure',
      capabilities: ['CI/CD Setup', 'Infrastructure Management', 'Monitoring', 'Auto-scaling'],
      advancedFeatures: ['Multi-cloud deployment', 'Zero-downtime updates', 'Self-healing systems']
    },
    integration: {
      name: 'Integration Expert',
      icon: GitBranch,
      color: 'from-pink-500 to-pink-700',
      description: 'Complex third-party integrations and APIs',
      capabilities: ['API Development', 'Protocol Integration', 'Data Synchronization', 'Webhook Management'],
      advancedFeatures: ['Auto-discovery', 'Rate limiting', 'Circuit breakers']
    }
  };

  const projectTypes = [
    { value: 'defi_protocol', label: 'DeFi Protocol', icon: BarChart3, complexity: 'High' },
    { value: 'token_launch', label: 'Token Launch', icon: Rocket, complexity: 'Medium' },
    { value: 'smart_contract', label: 'Smart Contract', icon: Code2, complexity: 'Medium' },
    { value: 'dapp', label: 'DApp', icon: MonitorSpeaker, complexity: 'High' },
    { value: 'nft_project', label: 'NFT Project', icon: Brain, complexity: 'Medium' },
    { value: 'dao', label: 'DAO', icon: Users, complexity: 'High' },
    { value: 'bridge', label: 'Cross-Chain Bridge', icon: GitBranch, complexity: 'Very High' },
    { value: 'trading_bot', label: 'Trading Bot', icon: Bot, complexity: 'High' }
  ];

  // Load agents and projects on component mount
  useEffect(() => {
    if (connected && publicKey) {
      loadAgents();
      loadProjects();
      initializeSystem();
    }
  }, [connected, publicKey]);

  const initializeSystem = async () => {
    try {
      setSystemStatus('initializing');
      // Simulate advanced system initialization
      setTimeout(() => setSystemStatus('active'), 3000);
    } catch (error) {
      console.error('Failed to initialize system:', error);
      setSystemStatus('error');
    }
  };

  const loadAgents = async () => {
    try {
      // Load projects to simulate agents
      const response = await axios.get(`${API}/projects`);
      setAgents(response.data.projects || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const allProjects = response.data.projects || [];
      
      // Filter by user wallet if connected
      if (connected && publicKey) {
        const userProjects = allProjects.filter(project => 
          project.user_wallet === publicKey.toString()
        );
        setProjects(userProjects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  // Handle clicking on a project to open it in Development Workspace
  const handleProjectClick = (project) => {
    if (project.id) {
      navigate(`/build/${project.id}`);
    } else {
      console.warn('Project missing ID:', project);
    }
  };

  const createAdvancedProject = async (e) => {
    e.preventDefault();
    if (!agentInput.trim()) return;

    setIsLoading(true);
    try {
      // ADVANCED-LEVEL INTENT DETECTION SYSTEM
    const input_lower = agentInput.toLowerCase().trim();
    
    // Enhanced question/guidance indicators with more sophisticated patterns
    const questionPatterns = [
      /^(what|how|why|where|when|which|who)\s+/,
      /\b(explain|describe|tell me about|help me understand|what is|what are|how do|how to|can you|should i|would you|could you)\b/,
      /\b(best practices?|best practice|advice|guidance|recommendation|suggestion|tip|tips|help|tutorial|guide)\b/,
      /\?\s*$/,  // Ends with question mark
      /\b(learn|understand|know|meaning|definition|teach|show me|demonstrate|clarify)\b/,
      /\b(difference between|compare|versus|vs|pros and cons|advantages|disadvantages)\b/,
      /\b(recommend|suggest|opinion|thoughts|consider|evaluate)\b/
    ];
    
    // Enhanced build/creation indicators with broader coverage
    const buildPatterns = [
      /^(create|build|make|develop|generate|design|code|implement|construct|craft)\s+/,
      /\bi want (to\s+)?(create|build|make|develop|generate|design|code|implement|have|get)\b/,
      /\blet['s]+ (create|build|make|develop|generate|design|code|implement)\b/,
      /\bcan you (create|build|make|develop|generate|design|code|implement|write|program)\b/,
      /\bplease (create|build|make|develop|generate|design|code|implement|write)\b/,
      /\bneed (a|an|to|some) (app|website|platform|tool|system|interface|dashboard)\b/,
      /\bwant (a|an|to|some) (app|website|platform|tool|system|interface|dashboard)\b/,
      /\b(app|website|platform|tool|system|interface|dashboard|application|dapp|defi|marketplace|exchange|trading|token)\s+(for|that|with|using)\b/
    ];

    // Platform-specific build indicators for Web3/Crypto/DeFi
    const web3BuildPatterns = [
      /\b(token|coin|cryptocurrency|nft|defi|dapp|smart contract|blockchain|web3|crypto)\b.*\b(create|build|make|develop|launch|deploy)\b/,
      /\b(dex|exchange|swap|trading|staking|farming|yield|liquidity|pool|lending|borrowing)\b/,
      /\b(wallet|marketplace|auction|voting|governance|dao|bridge|oracle)\b/,
      /\b(solana|ethereum|bitcoin|polygon|binance|avalanche|fantom|arbitrum)\b.*\b(app|dapp|platform)\b/,
      /\b(pump\.fun|uniswap|pancakeswap|compound|aave|opensea|raydium|serum|meteora)\b.*\b(like|similar|clone|alternative)\b/
    ];

    // E-commerce and SaaS indicators
    const ecommercePatterns = [
      /\b(store|shop|marketplace|ecommerce|e-commerce|retail|shopping|cart|checkout|payment)\b/,
      /\b(inventory|product|catalog|order|shipping|customer|vendor|seller)\b/,
      /\b(subscription|saas|software|tool|productivity|collaboration|project|management)\b/,
      /\b(dashboard|analytics|reporting|crm|erp|cms|blog|social)\b/
    ];

    // Check if this is a build request (Advanced Console defaults to building)
    const hasStrongBuildPattern = buildPatterns.some(pattern => pattern.test(input_lower));
    const hasWeb3BuildPattern = web3BuildPatterns.some(pattern => pattern.test(input_lower));
    const hasEcommercePattern = ecommercePatterns.some(pattern => pattern.test(input_lower));
    
    // Advanced Console should default to sophisticated building
    const isAdvancedBuildRequest = hasStrongBuildPattern || hasWeb3BuildPattern || hasEcommercePattern || agentInput.length > 20;
    
    console.log('🚀 Advanced Console Intent Detection:', {
      input: agentInput.slice(0, 60) + '...',
      hasStrongBuildPattern,
      hasWeb3BuildPattern,
      hasEcommercePattern,
      isAdvancedBuildRequest,
      selectedAgent: activeAgent,
      projectType: selectedProjectType
    });
      const response = await axios.post(`${API}/create-project`, {
        prompt: `[ADVANCED AI MODE - ${agentConfigs[activeAgent]?.name}] ${agentInput}
        
Project Type: ${selectedProjectType}
Quality Level: Enterprise
Advanced Features: ${agentConfigs[activeAgent]?.advancedFeatures.join(', ')}
Security Requirements: Maximum
Deployment: Multi-environment
Documentation: Comprehensive

Please create a production-ready implementation with:
- Complete error handling
- Security best practices
- Performance optimization
- Comprehensive testing
- Full documentation
- Deployment automation
`,
        model: selectedModel,  // Include selected model
        user_wallet: publicKey.toString()
      }, {
        timeout: 180000 // 3 minutes for advanced projects
      });

      if (response.data) {
        setAgentInput('');
        loadProjects();
        alert('🚀 Advanced project created successfully with enhanced capabilities!');
      } else {
        alert('Advanced project creation in progress. Check your projects for updates.');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      alert(error.response?.data?.detail || 'Failed to create advanced project');
    } finally {
      setIsLoading(false);
    }
  };

  const submitTask = async (agentType, description) => {
    try {
      // For now, use the chat endpoint as a task submission
      const response = await axios.post(`${API}/chat`, {
        message: `Task for ${agentType}: ${description}`,
        user_wallet: publicKey.toString()
      });

      // Add task to local state for tracking
      const newTask = {
        task_id: Date.now().toString(),
        agent_type: agentType,
        description: description,
        status: 'submitted',
        created_at: new Date().toISOString()
      };
      setTasks(prev => [newTask, ...prev]);

      return newTask.task_id;
    } catch (error) {
      console.error('Failed to submit task:', error);
      throw error;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'initializing': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'initializing': return Clock;
      case 'error': return AlertTriangle;
      default: return Activity;
    }
  };

  if (!connected) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <div className="text-center">
          <Brain className="h-16 w-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Connect Wallet to Access Advanced Console</h2>
          <p className="text-gray-400">Connect your Solana wallet to access advanced AI development capabilities</p>
        </div>

      {/* Credits Manager Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowCreditsModal(false)}
              className="absolute -top-2 -right-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-1 z-10"
            >
              <X className="h-4 w-4" />
            </button>
            <CreditsManager
              userCredits={userCredits}
              onPurchase={() => {
                refreshCredits();
                setShowCreditsModal(false);
              }}
            />
          </div>
        </div>
      )}
      </div>
    );
  }

  const StatusIcon = getStatusIcon(systemStatus);

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Home</span>
              </Link>
              <div className="flex items-center space-x-2">
                <Cpu className="h-8 w-8 text-purple-500" />
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
                    Advanced AI Console
                  </h1>
                  <p className="text-sm text-gray-400">Next-Generation Web3 Development Environment</p>
                </div>
              </div>
            </div>
            
              <div className="flex items-center space-x-3">
                <div className="text-xs text-gray-400">
                  Credits: <span className="text-purple-400 font-medium">{userCredits}</span>
                </div>
                <button
                  onClick={() => setShowCreditsModal(true)}
                  className="flex items-center space-x-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-xs transition-colors"
                >
                  <Plus className="h-3 w-3" />
                  <span>Buy Credits</span>
                </button>
              </div>
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${getStatusColor(systemStatus)}`}>
                <StatusIcon className="h-4 w-4" />
                <span className="text-sm font-medium capitalize">{systemStatus}</span>
              </div>
              <div className="text-sm text-gray-400">
                {projects.length} Projects
              </div>
            </div>
          </div>
        </div>

      {/* Credits Manager Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowCreditsModal(false)}
              className="absolute -top-2 -right-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-1 z-10"
            >
              <X className="h-4 w-4" />
            </button>
            <CreditsManager
              userCredits={userCredits}
              onPurchase={() => {
                refreshCredits();
                setShowCreditsModal(false);
              }}
            />
          </div>
        </div>
      )}
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Agent Selection Panel */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Bot className="h-5 w-5 mr-2 text-purple-400" />
                AI Specialists
              </h3>
              
              <div className="space-y-3">
                {Object.entries(agentConfigs).map(([key, config]) => {
                  const IconComponent = config.icon;
                  const isActive = activeAgent === key;
                  
                  return (
                    <motion.button
                      key={key}
                      onClick={() => setActiveAgent(isActive ? null : key)}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        isActive 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`p-2 rounded-lg bg-gradient-to-r ${config.color}`}>
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="font-medium text-white">{config.name}</h4>
                          <p className="text-sm text-gray-400 mt-1">{config.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {config.capabilities.slice(0, 2).map((capability, index) => (
                              <span
                                key={index}
                                className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded"
                              >
                                {capability}
                              </span>
                            ))}
                          </div>
                          {isActive && (
                            <div className="mt-2 pt-2 border-t border-gray-600">
                              <p className="text-xs text-purple-300 font-medium">Advanced Features:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {config.advancedFeatures.map((feature, index) => (
                                  <span
                                    key={index}
                                    className="text-xs bg-purple-600/20 text-purple-300 px-2 py-1 rounded"
                                  >
                                    {feature}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <ChevronRight className={`h-4 w-4 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Project Type Selection */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Project Type</h3>
              <select
                value={selectedProjectType}
                onChange={(e) => setSelectedProjectType(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {projectTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label} ({type.complexity})
                  </option>
                ))}
              </select>
              
              {selectedProjectType && (
                <div className="mt-3 p-3 bg-gray-800/50 rounded-lg">
                  <p className="text-xs text-gray-400">
                    Complexity: <span className="text-purple-400">{projectTypes.find(t => t.value === selectedProjectType)?.complexity}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    This will create an enterprise-grade implementation with advanced features.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Main Interaction Area */}
          <div className="lg:col-span-2">
            {/* AI Interface */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Terminal className="h-5 w-5 mr-2 text-green-400" />
                  Advanced AI Interface
                </h3>
                {activeAgent && (
                  <span className="text-sm bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full">
                    {agentConfigs[activeAgent]?.name}
                  </span>
                )}
              </div>

              <form onSubmit={createAdvancedProject} className="space-y-4">
                {/* AI Model Selector */}
                {connected && (
                  <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-300">AI Model:</span>
                        <select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          className="bg-gray-800 text-white rounded border border-gray-600 px-2 py-1 text-sm focus:border-purple-500 outline-none"
                        >
                          {Object.entries(claudeModelSettings).map(([key, config]) => (
                            <option key={key} value={key}>
                              {config.name} ({Math.ceil(50 * config.multiplier)} credits)
                            </option>
                          ))}
                        </select>
                        <span className={`text-xs px-2 py-1 rounded ${getBadgeColor(claudeModelSettings[selectedModel]?.badge)}`}>
                          {claudeModelSettings[selectedModel]?.badge}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 flex-1">
                        {claudeModelSettings[selectedModel]?.description}
                      </div>
                      <div className="text-xs text-cyan-400 font-medium">
                        Cost: {Math.ceil(50 * claudeModelSettings[selectedModel].multiplier)} credits
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <textarea
                    value={agentInput}
                    onChange={(e) => setAgentInput(e.target.value)}
                    placeholder={
                      activeAgent 
                        ? `Describe your ${agentConfigs[activeAgent]?.name.toLowerCase()} requirements in detail. Be specific about features, security requirements, and performance needs...`
                        : "Select an AI specialist and describe your advanced Web3 project requirements..."
                    }
                    className="w-full h-40 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    disabled={!activeAgent}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-400">
                    Cost: {Math.ceil(50 * claudeModelSettings[selectedModel].multiplier)} credits for advanced enterprise-grade development
                  </div>
                  <button
                    type="submit"
                    disabled={!activeAgent || !agentInput.trim() || isLoading}
                    className="bg-gradient-to-r from-purple-500 to-violet-500 text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creating Advanced Project...</span>
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        <span>Create with Advanced AI</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Projects Grid */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold flex items-center">
                <Database className="h-5 w-5 mr-2 text-blue-400" />
                Your Advanced Projects
              </h3>

              {projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {projects.map((project, index) => (
                    <motion.div
                      key={project.id || index}
                      className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors cursor-pointer"
                      whileHover={{ scale: 1.02 }}
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-white">{project.prompt ? project.prompt.slice(0, 50) + '...' : 'Untitled Project'}</h4>
                          <p className="text-sm text-gray-400">{project.status || 'In Progress'}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          project.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                          project.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                          'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {project.status || 'Active'}
                        </span>
                      </div>

                      <p className="text-sm text-gray-300 mb-4 line-clamp-2">{project.prompt || 'Advanced AI-generated project'}</p>

                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-400">
                          Created: {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'Recent'}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-purple-400">
                            Click to edit code →
                          </span>
                          {project.preview_url && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering the card click
                                window.open(project.preview_url, '_blank');
                              }}
                              className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              View Live →
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-900/50 border border-gray-800 rounded-xl">
                  <Brain className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-300 mb-2">No advanced projects yet</h4>
                  <p className="text-gray-500">Select an AI specialist and create your first enterprise-grade project</p>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Credits Manager Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowCreditsModal(false)}
              className="absolute -top-2 -right-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-1 z-10"
            >
              <X className="h-4 w-4" />
            </button>
            <CreditsManager
              userCredits={userCredits}
              onPurchase={() => {
                refreshCredits();
                setShowCreditsModal(false);
              }}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdvancedConsole;